import React, { useState, useEffect, useRef } from 'react';
import supportIcon from '../assets/sidebar-icons/support.svg';
import letterIcon from '../assets/icons-chat/letter.svg';
import reportIcon from '../assets/icons-chat/report.svg';
import homeIcon from '../assets/icons-chat/home.svg';
import chatIcon from '../assets/icons-chat/chat.svg';
import helpIcon from '../assets/icons-chat/help.svg';
import avatarIcon from '../assets/icons-chat/avatar.svg';
import { useAuth } from '../contexts/AuthContext';
import ChatInterface from './ChatInterface/ChatInterface';
import SupportChat from './SupportChat/SupportChat';

const ChatButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [showChatInterface, setShowChatInterface] = useState(false);
  const [showInlineChat, setShowInlineChat] = useState(false);
  const [inlineView, setInlineView] = useState<'ai' | 'support'>('ai');
  const [chatSessionId, setChatSessionId] = useState(0);
  const chatCardRef = useRef<HTMLDivElement>(null);
  const { authState } = useAuth();

  // Fermer la carte si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        chatCardRef.current &&
        !chatCardRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('button[title="Close Chat"]')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleChatClick = () => {
    setIsOpen(!isOpen);
    setActiveTab('home');
    setShowChatInterface(false);
    setShowInlineChat(false);
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'messages') {
      openInlineChat('ai');
    } else {
      setShowInlineChat(false);
    }
    setShowChatInterface(false);
  };

  const handleCloseChatInterface = () => {
    setShowChatInterface(false);
    setActiveTab('home');
  };

  const openInlineChat = (view: 'ai' | 'support') => {
    // Set view and bump session key first to force a fresh mount of the correct component
    setInlineView(view);
    setChatSessionId((id) => id + 1);
    // Then reveal the inline container on the Messages tab
    setActiveTab('messages');
    setShowInlineChat(true);
  };

  const handleChatWithAI = () => openInlineChat('ai');

  return (
    <>
      {/* Chat Interface - Full Screen */}
      {showChatInterface && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl h-full max-h-[90vh] bg-white rounded-lg shadow-2xl">
            <ChatInterface onClose={handleCloseChatInterface} />
          </div>
        </div>
      )}

      {/* Chat Card */}
      {isOpen && !showChatInterface && (
        <div
          ref={chatCardRef}
          className="fixed bottom-24 right-6 shadow-lg transition-all duration-300 overflow-hidden"
          style={{
            zIndex: 99999,
            width: '363px',
            height: '666px',
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 120px)',
            background: 'linear-gradient(135deg, #D6DDFF 0%, #FAFAFA 100%)',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header - only show when not in chat mode */}
          {!showInlineChat && (
            <div className="px-6 pt-8 pb-4 flex items-center justify-between">
              <div>
                <img src="/logoblack.png" alt="Flashback" className="h-8" />
              </div>
              <div className="flex -space-x-2">
                <img
                  src={avatarIcon}
                  alt="Avatar"
                  className="w-10 h-10 rounded-full border-2 border-white"
                />
                {authState?.user?.imageUrl ? (
                  <img
                    src={authState.user.imageUrl}
                    alt="Profile"
                    className="w-10 h-10 rounded-full border-2 border-white"
                    onError={(e) => {
                      const imgElement = e.currentTarget as HTMLImageElement;
                      imgElement.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-sm font-medium border-2 border-white">
                    {authState?.user?.name ? authState.user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Area */}
          {!showInlineChat ? (
            <>
              {/* Welcome Message */}
              <div className="px-6 pt-8">
                <h2
                  style={{
                    color: '#414651',
                    fontWeight: 600,
                    fontSize: '30px',
                    lineHeight: '38px',
                  }}
                >
                  Hi there
                </h2>
                <h2
                  style={{
                    color: '#0A0D12',
                    fontWeight: 600,
                    fontSize: '30px',
                    lineHeight: '38px',
                  }}
                >
                  How can we help?
                </h2>
              </div>

              {/* Action Buttons */}
              <div className="px-6 pt-10 flex flex-col gap-4 items-center">
                <button
                  onClick={() => {
                    openInlineChat('support');
                  }}
                  className="bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
                  style={{
                    width: '331px',
                    height: '72px',
                    borderRadius: '12px',
                    gap: '16px',
                    paddingTop: '16px',
                    paddingRight: '20px',
                    paddingBottom: '16px',
                    paddingLeft: '20px',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#181D27',
                        lineHeight: '20px',
                        textAlign: 'left',
                      }}
                    >
                      Send us a message
                    </div>
                    <div
                      style={{
                        color: '#535862',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textAlign: 'left',
                      }}
                    >
                      We typically reply in a few minutes
                    </div>
                  </div>
                  <img src={letterIcon} alt="Send message" className="w-6 h-6" />
                </button>

                <button
                  onClick={() => {
                    openInlineChat('support');
                  }}
                  className="bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
                  style={{
                    width: '331px',
                    height: '72px',
                    borderRadius: '12px',
                    gap: '16px',
                    paddingTop: '16px',
                    paddingRight: '20px',
                    paddingBottom: '16px',
                    paddingLeft: '20px',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#181D27',
                        lineHeight: '20px',
                        textAlign: 'left',
                      }}
                    >
                      Report an issue
                    </div>
                    <div
                      style={{
                        color: '#535862',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textAlign: 'left',
                      }}
                    >
                      Found a bug or have some feedback?
                    </div>
                  </div>
                  <img src={reportIcon} alt="Report issue" className="w-6 h-6" />
                </button>

                <button
                  onClick={handleChatWithAI}
                  className="bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
                  style={{
                    width: '331px',
                    height: '72px',
                    borderRadius: '12px',
                    gap: '16px',
                    paddingTop: '16px',
                    paddingRight: '20px',
                    paddingBottom: '16px',
                    paddingLeft: '20px',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#181D27',
                        lineHeight: '20px',
                        textAlign: 'left',
                      }}
                    >
                      Chat with AI
                    </div>
                    <div
                      style={{
                        color: '#535862',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textAlign: 'left',
                      }}
                    >
                      Get personalized storage recommendations
                    </div>
                  </div>
                  <img src={chatIcon} alt="Chat with AI" className="w-6 h-6" />
                </button>
              </div>
            </>
          ) : (
            /* Inline Chat Interface */
            <div className="flex-1 flex flex-col">
              {/* Chat Header with Logo */}
              <div className="px-6 pt-8 pb-4 flex items-center justify-between">
                <div>
                  <img src="/logoblack.png" alt="Flashback" className="h-8" />
                </div>
                <div className="flex -space-x-2">
                  <img
                    src={avatarIcon}
                    alt="Avatar"
                    className="w-10 h-10 rounded-full border-2 border-white"
                  />
                  {authState?.user?.imageUrl ? (
                    <img
                      src={authState.user.imageUrl}
                      alt="Profile"
                      className="w-10 h-10 rounded-full border-2 border-white"
                      onError={(e) => {
                        const imgElement = e.currentTarget as HTMLImageElement;
                        imgElement.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-sm font-medium border-2 border-white">
                      {authState?.user?.name ? authState.user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Content */}
              <div className="flex-1 min-h-0 px-4 overflow-hidden">
                {inlineView === 'ai' ? (
                  <ChatInterface
                    key={`chat-${inlineView}-${chatSessionId}`}
                    onClose={() => setShowInlineChat(false)}
                    isInline={true}
                  />
                ) : (
                  <SupportChat
                    key={`chat-${inlineView}-${chatSessionId}`}
                    onClose={() => setShowInlineChat(false)}
                    isInline={true}
                  />
                )}
              </div>
            </div>
          )}

          {/* Spacer */}
          {!showInlineChat && <div className="flex-grow"></div>}

          {/* Footer Navigation */}
          <div className="border-t border-gray-200 grid grid-cols-3 bg-white">
            <button
              onClick={() => handleTabClick('home')}
              className={`py-4 flex flex-col items-center justify-center text-gray-700 hover:bg-gray-50 ${
                activeTab === 'home' ? 'bg-blue-50 text-blue-600' : ''
              }`}
            >
              <img src={homeIcon} alt="Home" className="w-6 h-6" />
              <div className="text-xs mt-1">Home</div>
            </button>
            <button
              onClick={() => handleTabClick('messages')}
              className={`py-4 flex flex-col items-center justify-center text-gray-700 hover:bg-gray-50 ${
                activeTab === 'messages' ? 'bg-blue-50 text-blue-600' : ''
              }`}
            >
              <img src={chatIcon} alt="Messages" className="w-6 h-6" />
              <div className="text-xs mt-1">Messages</div>
            </button>
            <button
              onClick={() => handleTabClick('help')}
              className={`py-4 flex flex-col items-center justify-center text-gray-700 hover:bg-gray-50 ${
                activeTab === 'help' ? 'bg-blue-50 text-blue-600' : ''
              }`}
            >
              <img src={helpIcon} alt="Help" className="w-6 h-6" />
              <div className="text-xs mt-1">Help</div>
            </button>
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={handleChatClick}
        className="fixed bottom-6 right-6 rounded-full hover:scale-105 transition-all duration-300 flex items-center justify-center"
        style={{
          zIndex: 99999,
          width: '64px',
          height: '64px',
          padding: '8px',
          background: 'transparent',
          border: 'none',
        }}
        title={isOpen ? 'Close Chat' : 'Chat Support'}
      >
        {/* Cercle noir en arrière-plan quand ouvert */}
        {isOpen && (
          <div
            className="absolute rounded-full"
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#000000',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 0,
            }}
          />
        )}

        {isOpen ? (
          // Flèche blanche vers le bas
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        ) : (
          // Icône support originale
          <img
            src={supportIcon}
            alt="Chat Support"
            className="w-12 h-12"
            style={{
              width: '48px',
              height: '48px',
            }}
          />
        )}
      </button>
    </>
  );
};

export default ChatButton;
